import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiRefreshCw, FiDownload, FiShoppingCart, FiAlertTriangle, FiCheckCircle, FiClock, FiChevronDown, FiChevronUp, FiPlay, FiInfo,
} from 'react-icons/fi';
import css from './ConsultaNfs.module.css';
import cx from './ConsultaNfe.module.css';
import GrupoSequencia from './GrupoSequencia';
import {
  listarClientesNfce, listarNfces, listarExecucoesNfce, sincronizarClienteNfce, executarCapturaNfceTodos, sequenciaNfce, justificarNfce,
  removerJustificativaNfce, baixarNfce, urlZipNfce,
} from '../../services/nfceCaptura';

/* ── Consulta NFC-e (SP) ──
   NFC-e (cupom, modelo 65) EMITIDAS pelos clientes de SP, pelos Serviços de
   Apoio à Escrituração da SEFAZ-SP com o certificado A1 do cliente: a
   SEFAZ lista todas as chaves do período (até 100 dias) e o sistema baixa o
   XML de cada uma. Como a lista é completa, a aba Sequência mostra os
   números que não foram autorizados. */

const STATUS = {
  nunca: ['Ainda não sincronizado', css.stNeutro], rodando: ['Sincronizando...', css.stRodando], ok: ['Em dia', css.stOk],
  erro: ['Erro na última consulta', css.stErro], limite: ['SEFAZ-SP pediu pausa (consumo indevido): continua mais tarde', css.stAlerta],
  sem_certificado: ['Sem certificado', css.stErro], certificado_vencido: ['Certificado vencido', css.stErro], senha_invalida: ['Senha do certificado inválida', css.stErro],
};
const PENDENTES = ['erro', 'limite', 'sem_certificado', 'certificado_vencido', 'senha_invalida'];
const DOWNLOAD = { pendente: 'XML a baixar', erro: 'Falha ao baixar', expirada: 'Fora dos 100 dias' };
const API_SEQ = { justificar: justificarNfce, remover: removerJustificativaNfce };
const moeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' }) : '—');
const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const doc = (d) => {
  const x = String(d || '').replace(/\D/g, '');
  return x.length === 14 ? x.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : d || '';
};
const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

function SequenciaNfce({ filtroPeriodo, setErro }) {
  const [dados, setDados] = useState(null);
  const chave = JSON.stringify(filtroPeriodo);
  const carregar = useCallback(() => sequenciaNfce(filtroPeriodo).then(setDados).catch(e => setErro(e.message)), [chave]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setDados(null); carregar(); }, [carregar]);
  if (!dados) return <p className={css.vazio}>Analisando a numeração...</p>;
  return (
    <div className={css.seqWrap}>
      <p className={css.seqAjuda}>
        A SEFAZ-SP lista <strong>todas</strong> as NFC-e autorizadas no período, então número que falta na série não foi autorizado: inutilizado,
        pulado no PDV ou rejeitado. Cancelada conta como número usado. Se o número foi inutilizado, use <strong>Justificar</strong>.
      </p>
      {!dados.series.length && <p className={css.vazio}>Nenhuma NFC-e nesse período.</p>}
      {dados.series.map(g => (
        <GrupoSequencia key={g.serie} g={g} titulo={`Série ${g.serie}`} sub="NFC-e (modelo 65)" clienteId={filtroPeriodo.cliente_id} onMudou={carregar} setErro={setErro} api={API_SEQ} />
      ))}
    </div>
  );
}

function ConsultaNfce() {
  const [dados, setDados] = useState({ items: [], ambiente: 2 });
  const [verPendencias, setVerPendencias] = useState(false);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState(null);
  const [aba, setAba] = useState('notas');
  const [modoData, setModoData] = useState('mes');
  const [filtro, setFiltro] = useState({ competencia: mesAtual(), inicio: '', fim: '', situacao: 'ativa' });
  const [notas, setNotas] = useState({ items: [], totais: { quantidade: 0, valor: 0, icms: 0, pendentes: 0 } });
  const [carregando, setCarregando] = useState(false);
  const [execucoes, setExecucoes] = useState([]);
  const [verLog, setVerLog] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const sincronizandoAntes = useRef(false);

  const carregarClientes = useCallback(() => listarClientesNfce().then(setDados).catch(e => setErro(e.message)), []);
  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  const cliente = dados.items.find(c => c.id === sel) || null;
  const periodo = sel ? { cliente_id: sel, ...(modoData === 'mes' ? { competencia: filtro.competencia } : { inicio: filtro.inicio, fim: filtro.fim }) } : null;
  const filtroApi = periodo ? { ...periodo, situacao: filtro.situacao } : null;
  const chaveFiltro = JSON.stringify(filtroApi);

  const carregarNotas = useCallback(() => {
    if (!filtroApi) return Promise.resolve();
    setCarregando(true); setErro('');
    return listarNfces(filtroApi).then(setNotas).catch(e => setErro(e.message)).finally(() => setCarregando(false));
  }, [chaveFiltro]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarNotas(); }, [carregarNotas]);
  useEffect(() => { if (sel) listarExecucoesNfce(sel).then(r => setExecucoes(r.items || [])).catch(() => {}); }, [sel, notas]);

  const sincronizando = !!cliente?.sincronizando || dados.jobRodando;
  useEffect(() => {
    if (!sincronizando) {
      if (sincronizandoAntes.current) { sincronizandoAntes.current = false; carregarNotas(); }
      return undefined;
    }
    sincronizandoAntes.current = true;
    const t = setInterval(carregarClientes, 4000);
    return () => clearInterval(t);
  }, [sincronizando, carregarClientes, carregarNotas]);

  async function sincronizar() {
    setErro(''); setMsg('');
    try { await sincronizarClienteNfce(sel); setMsg('Sincronização iniciada. A primeira carga lista os últimos 100 dias e baixa os XML aos poucos (pode levar bastante tempo em quem emite muito).'); await carregarClientes(); }
    catch (e) { setErro(e.message); }
  }
  async function capturarTodos() {
    if (!window.confirm('Capturar agora as NFC-e de todos os clientes de SP com certificado? Roda em segundo plano, um cliente por vez.')) return;
    setErro(''); setMsg('');
    try { await executarCapturaNfceTodos(); setMsg('Captura de todos iniciada em segundo plano.'); carregarClientes(); } catch (e) { setErro(e.message); }
  }
  async function baixar(caminho, nome) {
    setErro('');
    try { await baixarNfce(caminho, nome); } catch (e) { setErro(e.message); }
  }

  const q = busca.trim().toLowerCase();
  const lista = dados.items.filter(c => !q || `${c.codigo} ${c.nome}`.toLowerCase().includes(q));
  const pendencias = dados.items.filter(c => PENDENTES.includes(c.status));
  const [stTexto, stCls] = STATUS[cliente?.sincronizando ? 'rodando' : (cliente?.status || 'nunca')] || STATUS.nunca;

  return (
    <div className={css.page}>
      <div className={css.header}>
        <div className={css.headerIcone}><FiShoppingCart size={20} /></div>
        <div>
          <h1 className={css.titulo}>Consulta NFC-e (SP)</h1>
          <p className={css.sub}>NFC-e emitidas pelos clientes de São Paulo, pelo serviço oficial da SEFAZ-SP (SAE NFC-e) com o certificado A1 de cada cliente</p>
        </div>
        <span className={dados.ambiente === 1 ? css.ambProd : css.ambHom}>{dados.ambiente === 1 ? 'Produção' : 'Homologação'}</span>
        <button type="button" className={css.btnSec} onClick={capturarTodos} disabled={dados.jobRodando}>
          <FiPlay size={13} /> {dados.jobRodando ? 'Capturando todos...' : 'Capturar todos agora'}
        </button>
      </div>

      <div className={cx.aviso}>
        <FiInfo size={14} />
        <span>São as <strong>saídas</strong> em NFC-e (cupom) de clientes de <strong>SP</strong>. A SEFAZ-SP guarda os <strong>últimos 100 dias</strong>; quem emite muito tem os XML baixados aos poucos, em mais de uma execução. Clientes que não emitem NFC-e aparecem sem notas.</span>
      </div>

      {pendencias.length > 0 && (
        <div className={css.pendencias}>
          <button type="button" onClick={() => setVerPendencias(v => !v)}>
            <FiAlertTriangle size={14} /> {pendencias.length} cliente(s) com pendência na captura
            {verPendencias ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </button>
          {verPendencias && (
            <ul>
              {pendencias.map(p => (
                <li key={p.id}><strong>{p.codigo} - {p.nome}</strong>: {(STATUS[p.status] || [p.status])[0]}{p.ultimo_erro ? ` — ${p.ultimo_erro}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {erro && <p className={css.erro}>{erro}</p>}
      {msg && <p className={css.ok}>{msg}</p>}

      <div className={css.corpo}>
        <aside className={css.lista}>
          <div className={css.busca}><FiSearch size={13} /><input placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} /></div>
          <p className={css.listaInfo}>{dados.items.length} cliente(s) de SP com certificado</p>
          {lista.map(c => {
            const [, cls] = STATUS[c.sincronizando ? 'rodando' : (c.status || 'nunca')] || STATUS.nunca;
            return (
              <button key={c.id} type="button" className={`${css.itemCliente} ${sel === c.id ? css.itemAtivo : ''}`} onClick={() => setSel(c.id)}>
                <span className={`${css.ponto} ${cls}`} />
                <span className={css.itemTexto}>
                  <strong>{c.codigo} - {c.nome}</strong>
                  <small>{c.notas} NFC-e{c.pendentes ? ` · ${c.pendentes} a baixar` : ''} · {c.ultima_sincronizacao ? quando(c.ultima_sincronizacao) : 'nunca sincronizado'}</small>
                </span>
              </button>
            );
          })}
          {!lista.length && <p className={css.vazio}>Nenhum cliente de SP com certificado A1 cadastrado.</p>}
        </aside>

        <section className={css.detalhe}>
          {!cliente ? (
            <div className={css.placeholder}><FiShoppingCart size={34} /><p>Escolha um cliente para ver as NFC-e.</p></div>
          ) : (
            <>
              <div className={css.cliTopo}>
                <div>
                  <h2>{cliente.codigo} - {cliente.nome}</h2>
                  <p>{doc(cliente.cnpj)} · certificado vence {cliente.certificado_vence ? cliente.certificado_vence.split('-').reverse().join('/') : '—'} · listado até {quando(cliente.ultima_listagem)}</p>
                  <span className={`${css.status} ${stCls}`}>{stTexto}{cliente.status !== 'ok' && cliente.ultimo_erro ? `: ${cliente.ultimo_erro}` : ''}</span>
                </div>
                <button type="button" className={css.btnPri} onClick={sincronizar} disabled={sincronizando}>
                  <FiRefreshCw size={13} className={sincronizando ? css.girando : ''} /> {sincronizando ? 'Sincronizando...' : 'Sincronizar agora'}
                </button>
              </div>

              <div className={css.abas}>
                <button type="button" className={aba === 'notas' ? css.abaAtiva : ''} onClick={() => setAba('notas')}>NFC-e</button>
                <button type="button" className={aba === 'sequencia' ? css.abaAtiva : ''} onClick={() => setAba('sequencia')}>Sequência por série</button>
              </div>

              <div className={css.filtros}>
                <div className={css.segmento}>
                  <button type="button" className={modoData === 'mes' ? css.segAtivo : ''} onClick={() => setModoData('mes')}>Mês</button>
                  <button type="button" className={modoData === 'periodo' ? css.segAtivo : ''} onClick={() => setModoData('periodo')}>Período</button>
                </div>
                {modoData === 'mes' ? (
                  <input type="month" className={css.input} value={filtro.competencia} onChange={e => setFiltro(f => ({ ...f, competencia: e.target.value }))} title="Mês da emissão" />
                ) : (
                  <>
                    <input type="date" className={css.input} value={filtro.inicio} onChange={e => setFiltro(f => ({ ...f, inicio: e.target.value }))} />
                    <span className={css.ate}>até</span>
                    <input type="date" className={css.input} value={filtro.fim} onChange={e => setFiltro(f => ({ ...f, fim: e.target.value }))} />
                  </>
                )}
                {aba === 'notas' && (<>
                  <select className={css.input} value={filtro.situacao} onChange={e => setFiltro(f => ({ ...f, situacao: e.target.value }))}>
                    <option value="ativa">Autorizadas</option>
                    <option value="cancelada">Canceladas</option>
                    <option value="pendente">XML ainda não baixado</option>
                    <option value="todas">Todas</option>
                  </select>
                  <span className={css.espaco} />
                  <button type="button" className={css.btnPri} disabled={!notas.items.length} title="XML das autorizadas na raiz; canceladas na pasta canceladas/ com o evento"
                    onClick={() => baixar(urlZipNfce(filtroApi), 'nfce.zip')}>
                    <FiDownload size={13} /> Baixar ZIP
                  </button>
                </>)}
              </div>

              {aba === 'sequencia' ? <SequenciaNfce filtroPeriodo={periodo} setErro={setErro} /> : (<>
                <div className={cx.totais}>
                  <div><span>NFC-e</span><strong>{notas.totais.quantidade}</strong></div>
                  <div><span>Valor (autorizadas)</span><strong>{moeda(notas.totais.valor)}</strong></div>
                  <div><span>ICMS</span><strong>{moeda(notas.totais.icms)}</strong></div>
                  <div><span>XML a baixar</span><strong>{notas.totais.pendentes}</strong></div>
                </div>
                <div className={css.tabelaWrap}>
                  <table className={css.tabela}>
                    <thead><tr><th>Série</th><th>Nº</th><th>Emissão</th><th className={css.num}>Valor</th><th className={css.num}>ICMS</th><th>Situação</th><th>XML</th><th /></tr></thead>
                    <tbody>
                      {carregando ? <tr><td colSpan={8} className={css.vazio}>Carregando...</td></tr>
                        : !notas.items.length ? <tr><td colSpan={8} className={css.vazio}>Nenhuma NFC-e nesse filtro.</td></tr>
                          : notas.items.map(n => (
                            <tr key={n.id} className={n.situacao !== 'autorizada' ? css.linhaCancelada : ''}>
                              <td>{n.serie}</td>
                              <td>{n.numero}</td>
                              <td>{n.data_emissao ? dataHora(n.data_emissao) : `${n.mes_chave.split('-').reverse().join('/')}`}</td>
                              <td className={css.num}>{n.valor_total != null ? moeda(n.valor_total) : '—'}</td>
                              <td className={css.num}>{n.valor_icms != null ? moeda(n.valor_icms) : '—'}</td>
                              <td>{n.situacao === 'autorizada' ? <span className={css.sAtiva}><FiCheckCircle size={11} /> Autorizada</span> : <span className={css.sCancelada}>Cancelada</span>}</td>
                              <td>{n.status_download === 'ok' ? <span className={cx.completo}>Baixado</span> : <span className={cx.resumo} title={n.erro || ''}>{DOWNLOAD[n.status_download]}</span>}</td>
                              <td className={css.acoes}>{n.status_download === 'ok' && <button type="button" onClick={() => baixar(`/notas/${n.id}/xml`, `${n.chave_acesso}.xml`)}>XML</button>}</td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
                {notas.limitado && <p className={css.listaInfo}>Mostrando as 10.000 primeiras; use um período menor para ver todas.</p>}
              </>)}

              <div className={css.log}>
                <button type="button" onClick={() => setVerLog(v => !v)}><FiClock size={13} /> Execuções deste cliente {verLog ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}</button>
                {verLog && (
                  <table className={css.tabela}>
                    <thead><tr><th>Início</th><th>Origem</th><th>Chaves novas</th><th>XML baixados</th><th>Eventos</th><th>Erros</th><th>Duração</th><th>Mensagem</th></tr></thead>
                    <tbody>
                      {execucoes.map(e => (
                        <tr key={e.id}>
                          <td>{quando(e.inicio)}</td><td>{e.origem}</td><td>{e.chaves_novas}</td><td>{e.baixadas}</td><td>{e.eventos}</td><td>{e.erros}</td>
                          <td>{e.duracao_ms != null ? `${Math.round(e.duracao_ms / 1000)}s` : '…'}</td><td className={css.msgLog}>{e.mensagem || '—'}</td>
                        </tr>
                      ))}
                      {!execucoes.length && <tr><td colSpan={8} className={css.vazio}>Nenhuma execução ainda.</td></tr>}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default ConsultaNfce;
